import posthog from 'posthog-js';
import { FaRegHeart } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { memo, useRef, type JSX } from 'react';
import { ProductImage } from '@/components/ui';
import {
  MAKRKETPLACE_IMAGE_MAP,
  type MarketplaceKey,
} from '@/constants/marketplaceImage';
import useDownloadAppSheetStore from '@/stores/DownloadAppSheetStore';
import type { Product } from '@/types/Product.types';
import {
  formatIndianPrice,
  toTitleCase,
  updatedImageUrl,
} from '@/utils/formatting';
import { openProductInApp } from '@/utils/openInApp';
import styles from './ProductCard.module.css';

const ProductCard = ({
  product,
  disableNavigation = false,
}: {
  product: Product;
  disableNavigation?: boolean;
}): JSX.Element => {
  const location = useLocation();
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { openSheet: openDownloadSheet } = useDownloadAppSheetStore();

  const handleLikeClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    openProductInApp({
      productId: product.id,
      fallback: () => {
        openDownloadSheet('personalize');
      },
    });
  };

  const getProductImage = (): string => {
    return updatedImageUrl(product.imageUrl);
  };

  const getProductBrand = (): string => {
    return toTitleCase(product.brand || '');
  };

  const getProductName = (): string => {
    return product.name || '';
  };

  const getProductPrice = (): string => {
    return formatIndianPrice(product.discountPrice || product.price || '');
  };

  const getProductMRP = (): string => {
    return formatIndianPrice(product.price || '');
  };

  const isProductAvailable = (): boolean => {
    return product.isAvailable || false;
  };

  const getMarketplaceImage = (): string | undefined => {
    if (product.marketplace && product.marketplace in MAKRKETPLACE_IMAGE_MAP) {
      return MAKRKETPLACE_IMAGE_MAP[product.marketplace as MarketplaceKey];
    } else if (product.brand in MAKRKETPLACE_IMAGE_MAP) {
      return MAKRKETPLACE_IMAGE_MAP[product.brand as MarketplaceKey];
    }
    return undefined;
  };

  const handleCardClickEvent = (): void => {
    if (disableNavigation) return;

    const urlParams = new URLSearchParams(location.search);
    const curationId = urlParams.get('curation_id');
    const curatorName =
      urlParams.get('curatorName') || urlParams.get('curator_name');
    const key = urlParams.get('key');
    const rid = urlParams.get('rid');
    const variant = urlParams.get('variant');
    const fbclid = urlParams.get('fbclid');

    posthog.capture('product_card_click', {
      component_name: product.id,
      product_id: product.id,
      product_name: product.name,
      brand: product.brand,
      curation_id: curationId || '',
      curator_name: curatorName || '',
      key: key || '',
      rid: rid || '',
      variant: variant || '',
      fbclid: fbclid || '',
      source:
        location.pathname === '/curator-store' ? 'curator_store' : 'homepage',
    });
  };
  const getDiscountPercentage = (): number | null => {
    const mrp = parseFloat(product.price || '0');
    const discount = parseFloat(product.discountPrice || '0');

    if (mrp > 0 && discount > 0 && discount < mrp) {
      const percent = ((mrp - discount) / mrp) * 100;
      return Math.round(percent);
    }
    return null;
  };

  const CardContent = (
    <>
      <div ref={containerRef} className={styles.imageWrapper}>
        <ProductImage
          ref={imgRef}
          src={getProductImage()}
          alt={getProductName()}
        />
        <button
          className={styles.likeButton}
          aria-label={'Like'}
          onClick={handleLikeClick}
        >
          <FaRegHeart color="#080928" />
        </button>

        {getMarketplaceImage() && (
          <img
            src={getMarketplaceImage()}
            alt={product.marketplace || product.brand}
            className={styles.brandRatingContainer}
          />
        )}
      </div>

      <div className={styles.productDescriptionContainer}>
        <p className={styles.productText}>
          <span className={styles.brandNameText}>{getProductBrand()}</span>
          <span> - </span>
          <span>{getProductName()}</span>
        </p>
      </div>
      {!isProductAvailable() && (
        <div className={styles.outOfStockButton}>Out of Stock</div>
      )}
      <div className={styles.productInfo}>
        <div className={styles.productInfoContainer}>
          <div className={styles.priceContainer}>
            <p className={styles.priceText}>₹{getProductPrice()}</p>

            {product.price && product.price !== product.discountPrice && (
              <>
                <p className={styles.mrpText}>
                  <span>₹{getProductMRP()}</span>
                </p>
                {getDiscountPercentage() !== null && (
                  <p className={styles.discountText}>
                    {getDiscountPercentage()}% off
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (disableNavigation) {
    return <div className={styles.cardContainer}>{CardContent}</div>;
  }

  return (
    <Link
      to={`/product/${product.id}${location.search}`}
      state={{
        productData: product,
        timestamp: Date.now(),
        previousPath: location.pathname,
      }}
      onClick={handleCardClickEvent}
    >
      {CardContent}
    </Link>
  );
};

export default memo(ProductCard);
